/**
 * Live aggregate of shares/{token}/participants for an owner's share links (replaces dealRoomMembers).
 */
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../config/firebase';

const SHARES = 'shares';
const PARTICIPANTS = 'participants';

/**
 * @param {string[]} shareTokens
 * @param {(rows: Array<{ uid: string, displayName?: string, shareTokens: string[] }>) => void} onRows
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeAggregatedShareParticipants(shareTokens, onRows, onError) {
  const tokens = [...new Set((shareTokens || []).map((t) => String(t || '').trim()).filter(Boolean))];
  if (!tokens.length) {
    onRows([]);
    return () => {};
  }

  /** @type {Record<string, Map<string, string>>} */
  const perToken = {};
  tokens.forEach((t) => {
    perToken[t] = new Map();
  });

  const emit = () => {
    /** @type {Map<string, { uid: string, displayName: string, shareTokens: string[] }>} */
    const byUid = new Map();
    for (const tok of tokens) {
      const m = perToken[tok];
      if (!m) continue;
      for (const [uid, displayName] of m) {
        let row = byUid.get(uid);
        if (!row) {
          row = { uid, displayName: '', shareTokens: [] };
          byUid.set(uid, row);
        }
        if (!row.shareTokens.includes(tok)) row.shareTokens.push(tok);
        const dn = String(displayName || '').trim();
        if (dn && !row.displayName) row.displayName = dn;
      }
    }
    onRows([...byUid.values()]);
  };

  const unsubs = tokens.map((tok) =>
    onSnapshot(
      collection(firestore, SHARES, tok, PARTICIPANTS),
      (snap) => {
        const m = new Map();
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          m.set(d.id, typeof data.displayName === 'string' ? data.displayName : '');
        });
        perToken[tok] = m;
        emit();
      },
      (err) => {
        console.warn('subscribeAggregatedShareParticipants', tok, err?.code, err?.message);
        onError?.(err);
      },
    ),
  );

  return () => unsubs.forEach((u) => u());
}
